import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const RELATIONSHIPS: InsightSummaryPage = {
  page_id: 'insights-23',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Relationships',
      body: 'Mediators take the care and feeding of their relationships seriously. Their connections emerge in a variety of ways and include the cultivation of client connections, teaming with colleagues across departments, and mentoring new mediators into the professional fold.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'The advent of remote meeting technology becoming a vital tool for intra-agency communication and providing effective and satisfying service to clients was <Embed getEmbedInfo={getEmbedInfo}>abrupt and challenging-5249111</Embed>. As the agency adapted to the new working environment, technology became a vehicle for connection between FMCS members across the nation. People organically came to know, support, teach and collaborate with their colleagues, many of whom they never met before during their time with the agency. These <Embed getEmbedInfo={getEmbedInfo}>fresh interactions-5250344</Embed> gave birth to new friendships, co-worker support systems, unique client service opportunities and creative collaborations which often produced superior customer experiences with FMCS.<br/><br/>Recommendations from conversations include:<br/><ul><li>Field operations management <Embed getEmbedInfo={getEmbedInfo}>prioritizes teamwork-5250372</Embed> and cross-team collaboration so that mediators not only pool their expertise to provide excellent service, but build morale and uplift the quality of the mediators work.</li><li>Administrative Leadership promotes & supports nationwide <Embed getEmbedInfo={getEmbedInfo}>connection & collaboration-5249388</Embed>.</li><li>Management <Embed getEmbedInfo={getEmbedInfo}>encourages and supports-5250464</Embed> mediators connecting with each other within regional geography and across the country, regardless of location.</li><li>More encouraging & supportive interactions and consistent communication between leadership and all levels of staff.</li></ul>',
        },
        {
          label: 'Teamwork',
          text: "Across the board, mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker KA-5250351</Embed> express their reliance on teamwork to maximize their effectiveness and to facilitate problem solving. Interestingly they found that even in the midst of the pandemic, teamwork in the virtual space was a surprising result from an entirely in-person experience. This was also attested to by <Embed getEmbedInfo={getEmbedInfo}>Speaker SA:-5250342</Embed> “Teaming together on projects, working on solving all kinds of interesting problems, and for me that was a life changing experience and I don't think it could have ever happened for me, at least in an in-person only situation, because it was so sporadic, we're so spread apart, and the time that it takes to actually get together in the same room with people is a big investment.” <br/><br/>We see that for mediators, particularly in leadership positions, who worried that being relegated to virtual interactions would stymie their effectiveness, that this was not the case. In fact, <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5249325</Embed> describes the “high degree of intentionality” that comes from coordinating with other leaders and the ease of instant video conferencing connections: “...I was normally the team leader, but again, what's so beautiful about the virtual condition is that it never shakes out that way, it really takes all four of us operating with an equally high level of responsibility and accountability... So I found that prep meetings with that four mediator team needed to happen all the time, sometimes multiple times a week.”",
        },
        {
          label: 'Disconnection',
          text: "Mediators struggled during pandemic restrictions to create and sustain healthy connections with their colleagues and clients. Their common refrain is summarized by those like <Embed getEmbedInfo={getEmbedInfo}>Speaker BB-5248966</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250345</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker LE-5249100</Embed> who feel restricted in the virtual negotiation room: “I was really concerned about how to read the mood of the clients and read the room in a way that you do in person naturally.” Time and again, mediators find strategies to bridge the distance like <Embed getEmbedInfo={getEmbedInfo}>Speaker MB-5248126</Embed>. Even mediators who identify as natural introverts like <Embed getEmbedInfo={getEmbedInfo}>Speaker TA-5248124</Embed> recognized that “...we all need to get out of our homes and be with each other. I think it's vital to us.” For some, it was simply a matter of the “loneliness” as described by <Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249063</Embed>.<br/><br/>Specifically, the challenge of providing excellent service in a remote digital environment is exacerbated by existing divisions among departments. <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5249390</Embed> sums up the divisions as , “...this (virtual) environment has kind of put us into some silos, more than it had been before. I think by that I mean there are less opportunities for us to engage with one another in person and even sometimes in groups as an agency.” <br/><br/>Like the silos mentioned by Speaker TC, some disconnections existed before the virtual disconnections but had been made even more challenging during the pandemic. <Embed getEmbedInfo={getEmbedInfo}>Speaker LC-5248948</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker BC-5249026</Embed> observe how they manage their role as mediators when already existing differences are further changed when negotiations are done remotely.",
        },
        {
          label: 'Connection',
          text: "In the conversations, mediators share the many ways they keep their client and coworker relationships active and engaged. One seasoned mediator, <Embed getEmbedInfo={getEmbedInfo}>Speaker SC-5250623</Embed> had to quickly embrace the use of ice-breakers to build rapport among negotiating parties when he didn't have the benefit of orchestrating in-person connections. <br/><br/>Nearly all participants in the conversations express gratitude for the unique opportunity to work with colleagues to build new or renew existing relationships. Along with <Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249062</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker WA-5249061</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker DB-5249066</Embed>, and <Embed getEmbedInfo={getEmbedInfo}>Speaker CD-5250595</Embed> who all enjoyed forging new relationships and strengthening their current ones, <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5249388</Embed> summarizes that they’ve enjoyed most, “...the opportunity and the privilege of being able to connect with so many mediators throughout the country and so many different staff members in so many different places. It amazes me.”<br/><br/>However, most mediators, while recognizing the extraordinary connections that came from remote work that introduced new partnerships across the agency, also viewed the online interactions as springboards to in-person connections rather than just static virtual relationships. <Embed getEmbedInfo={getEmbedInfo}>Speaker MC-5250335</Embed> “... what I'm looking forward to is being able to go out and about doing EAOs in-person, even just grabbing lunch, talking to some people that I've worked with extensively now, but haven't been able to shake their hand.” <br/><br/>Creating connections in challenging times is not a new experience for mediators, and participants like <Embed getEmbedInfo={getEmbedInfo}>Speaker CC-5249084</Embed> focus instead on the universal desire to be part of a “caring community” where colleagues, clients, and the wider public can meet.",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Relationships',
    page_id: 'insights-23',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
